/**
 * v3.15-1167 — "Ask Ghost" public floating assistant.
 *
 * Persistent on every public page. Helps visitors understand Consumer
 * Funding (EasyQuickFund), Business Funding, CreditForward360, the
 * Application Concierge, and the partner program. Captures the question,
 * routes to a Concierge via mailto, and links out to the dedicated /ghost
 * page for deeper context. No backend, no auth — purely a public lead
 * funnel.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { X, Send, ArrowRight } from 'lucide-react';
import GhostLogo from './GhostLogo';

const QUICK_PROMPTS = [
  'How much consumer funding can my client qualify for?',
  'Tell me about business funding.',
  'What is CreditForward360?',
  'How does the Application Concierge work?',
  'How do I become a partner?',
];

const AskGhostPublic = () => {
  const [open, setOpen] = useState(false);
  const [msg, setMsg] = useState('');

  const submit = (text) => {
    const body = encodeURIComponent(text);
    window.location.href = `mailto:support@prestigiouscompanies.org?subject=Ask%20Ghost%20%E2%80%94%20Funding%20question&body=${body}`;
    setOpen(false);
    setMsg('');
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    submit(msg);
  };

  return (
    <>
      {/* Floating launcher */}
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="fixed bottom-6 right-6 z-40 group inline-flex items-center gap-2 bg-slate-900 text-white font-semibold pl-2 pr-4 py-2 rounded-full shadow-2xl hover:bg-black transition-all border border-gold/40 ring-1 ring-black/5"
        data-testid="public-ask-ghost-btn"
        aria-label="Ask Ghost"
      >
        <span className="rounded-full bg-black/60 p-1.5 group-hover:bg-black transition-colors">
          <GhostLogo size="sm" testId="ask-ghost-btn-logo" />
        </span>
        <span className="hidden sm:inline tracking-wide">Ask Ghost</span>
      </button>

      {open && (
        <div
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4 bg-slate-900/50"
          onClick={() => setOpen(false)}
          data-testid="ask-ghost-overlay"
        >
          <div
            className="bg-white rounded-lg w-full max-w-md shadow-2xl border border-slate-200"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="bg-slate-900 text-white px-5 py-4 rounded-t-lg flex items-center justify-between border-b border-gold/30">
              <div className="flex items-center gap-3">
                <GhostLogo size="md" />
                <div>
                  <p className="font-heading text-sm font-bold leading-none">Ghost<sup className="text-[8px]">™</sup></p>
                  <p className="text-[10px] uppercase tracking-[0.2em] text-gold/80 mt-1">AI Operations Assistant</p>
                </div>
              </div>
              <button
                onClick={() => setOpen(false)}
                aria-label="Close"
                className="text-white/70 hover:text-white"
                data-testid="ask-ghost-close"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {/* Body */}
            <div className="p-5 space-y-4">
              <p className="text-sm text-slate-700 leading-relaxed">
                I&rsquo;m the intelligence layer behind Prestigious Hub. I answer questions about{' '}
                <strong>Consumer Funding (EasyQuickFund™)</strong>, <strong>Business Funding</strong>,{' '}
                <strong>CreditForward360</strong>, the <strong>Application Concierge</strong>, and the partner program — then route you to a human Concierge.
              </p>

              {/* Quick prompts */}
              <div className="flex flex-wrap gap-2" data-testid="ask-ghost-prompts">
                {QUICK_PROMPTS.map((p) => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => submit(p)}
                    className="text-[11px] bg-slate-100 hover:bg-slate-200 border border-slate-200 text-slate-700 px-2.5 py-1 rounded-full transition-colors"
                    data-testid={`ask-ghost-prompt-${p.slice(0, 12).toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                  >
                    {p}
                  </button>
                ))}
              </div>

              {/* Free-form question */}
              <form onSubmit={handleSubmit} className="space-y-3">
                <textarea
                  value={msg}
                  onChange={(e) => setMsg(e.target.value)}
                  required
                  minLength={5}
                  rows={3}
                  placeholder="Ask anything — soft-pull pre-approvals, lender matching, partner pricing…"
                  className="w-full text-sm border border-slate-300 rounded p-3 focus:outline-none focus:ring-2 focus:ring-primary/30"
                  data-testid="ask-ghost-input"
                />
                <button
                  type="submit"
                  className="w-full inline-flex justify-center items-center gap-2 bg-gradient-gold text-slate-900 font-semibold py-2.5 rounded hover:shadow-gold transition-all"
                  data-testid="ask-ghost-submit"
                >
                  Send to a Concierge <Send className="h-4 w-4" />
                </button>
              </form>

              <Link
                to="/ghost"
                onClick={() => setOpen(false)}
                className="block text-center text-xs text-primary hover:underline inline-flex items-center justify-center gap-1 w-full"
                data-testid="ask-ghost-learn-more"
              >
                Learn more about Ghost Intelligence <ArrowRight className="h-3 w-3" />
              </Link>

              <p className="text-[11px] text-slate-500 text-center leading-relaxed">
                A human Application Concierge follows up — usually within one business hour.
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AskGhostPublic;
